import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PerformanceTest } from './api';

interface TestsTableProps {
    tests: PerformanceTest[];
    deletingId: string | null;
    onEdit: (tst: PerformanceTest) => void;
    onDelete: (id: string) => void;
}

const TestsTable: React.FC<TestsTableProps> = ({ tests, deletingId, onEdit, onDelete }) => {
    const { t } = useTranslation();
    return (
    <>
    {/* Mobile cards — see RecordsTable: five px-4 columns cannot fit a phone,
        so the table below is md+ only and untouched. */}
    <div className="md:hidden divide-y divide-cyan-100">
        {tests.length === 0 ? (
            <p className="px-1 py-6 text-center text-slate-400 text-xs italic">
                {t('personalTest.emptyTests')}
            </p>
        ) : (
            tests.map((tst) => (
                <div key={tst.id} className="py-3 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-700 truncate">{tst.testName}</div>
                        <div className="flex items-baseline gap-2 mt-0.5">
                            <span className="text-sm text-cyan-700 font-semibold truncate">{tst.result}</span>
                            <span className="text-xs text-slate-400 flex-shrink-0">{tst.performedAt}</span>
                        </div>
                        {tst.notes && (
                            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{tst.notes}</div>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={() => onEdit(tst)}
                            aria-label={t('common.edit')}
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 active:bg-slate-100"
                        >
                            <i className="fa-solid fa-pen text-sm"></i>
                        </button>
                        <button
                            onClick={() => onDelete(tst.id)}
                            disabled={deletingId === tst.id}
                            aria-label={t('common.delete')}
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 active:bg-red-50 disabled:opacity-50"
                        >
                            {deletingId === tst.id
                                ? <i className="fa-solid fa-spinner fa-spin text-sm"></i>
                                : <i className="fa-solid fa-trash text-sm"></i>}
                        </button>
                    </div>
                </div>
            ))
        )}
    </div>

    <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm text-left">
            <thead className="bg-cyan-50 text-cyan-700 uppercase font-bold text-xs">
                <tr>
                    <th className="px-4 py-3 rounded-l-lg">{t('personalTest.colTest')}</th>
                    <th className="px-4 py-3">{t('personalTest.colResult')}</th>
                    <th className="px-4 py-3">{t('common.date')}</th>
                    <th className="px-4 py-3">{t('common.notes')}</th>
                    <th className="px-4 py-3 rounded-r-lg w-8"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-cyan-100">
                {tests.length === 0 ? (
                    <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-slate-400 text-xs italic">
                            {t('personalTest.emptyTests')}
                        </td>
                    </tr>
                ) : (
                    tests.map((tst) => (
                        <tr key={tst.id} className="hover:bg-cyan-50 transition-colors group">
                            <td className="px-4 py-3 font-bold text-slate-700">{tst.testName}</td>
                            <td className="px-4 py-3 text-slate-700">{tst.result}</td>
                            <td className="px-4 py-3 text-slate-700">{tst.performedAt}</td>
                            <td className="px-4 py-3 text-slate-700">{tst.notes || '—'}</td>
                            <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => onEdit(tst)}
                                            title={t('common.edit')}
                                            className="text-slate-300 hover:text-blue-500"
                                        >
                                            <i className="fa-solid fa-pen text-xs"></i>
                                        </button>
                                        <button
                                            onClick={() => onDelete(tst.id)}
                                            disabled={deletingId === tst.id}
                                            title={t('common.delete')}
                                            className="text-slate-300 hover:text-red-500 disabled:opacity-50"
                                        >
                                            {deletingId === tst.id
                                                ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                                : <i className="fa-solid fa-trash text-xs"></i>}
                                        </button>
                                    </div>
                                </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
    </>
    );
};

export default TestsTable;
